/**
 * The folder's AI summary, as a panel that rises from the bottom of the
 * workspace instead of a clamped card sitting on top of the file list.
 *
 * The summary is the most-written and least-read thing on this page: three
 * clipped lines above the table were easy to ignore and impossible to read.
 * A person now sees one line of it at the foot of the workspace and opens it
 * deliberately, at a size where the whole thing actually fits.
 *
 * The text arrives structured — "When to search", "Topics", "Trigger phrases",
 * then a file list — so it is rendered as those parts rather than as a wall of
 * characters. Anything that does not parse is shown verbatim: an unfamiliar
 * shape must never mean an empty panel.
 */
import React, { useEffect, useMemo } from 'react';
import { useLanguage } from '../../../hooks/useLanguage';
import './VaultSummarySheet.css';

interface Props {
  /** The heading: "Folder summary" by default, "File summary" in a file window. */
  titleKey?: string;
  folderName: string;
  summary: string;
  updatedAt?: string | null;
  open: boolean;
  onToggle: (open: boolean) => void;
}

interface Section {
  label: string | null;
  body: string;
  items: string[];
}

const LABELLED = /^(?:\*\*)?([A-Za-z][A-Za-z /-]{2,30})(?:\*\*)?\s*:\s*(.*)$/;
const HEADING = /^#{1,6}\s+/;
const BULLET = /^[-*•]\s+/;
/** Labels whose value reads as a set of short phrases, not a sentence. */
const CHIP_LABELS = /^(topics|trigger phrases|keywords|tags)$/i;

/** The generator emits light markdown. Nothing here renders HTML, so the
 *  emphasis markers are stripped rather than honoured — a stray `**` printed
 *  literally is the kind of detail that makes a panel look broken. */
const plain = (s: string) => s.replace(/\*\*/g, '').replace(/(^|\s)\*(\S)/g, '$1$2').trim();

function parse(summary: string): Section[] {
  const sections: Section[] = [];
  let current: Section | null = null;

  for (const raw of summary.split('\n')) {
    const line = plain(raw);
    if (!line) continue;
    if (HEADING.test(line)) continue; // the folder's own name is already in the header

    if (BULLET.test(line)) {
      const item = plain(line.replace(BULLET, ''));
      if (!item) continue;
      if (current) current.items.push(item);
      else sections.push({ label: null, body: '', items: [item] });
      continue;
    }

    const match = line.match(LABELLED);
    if (match) {
      current = { label: plain(match[1]), body: plain(match[2]), items: [] };
      sections.push(current);
      continue;
    }

    if (current && !current.items.length) {
      current.body = current.body ? `${current.body} ${line}` : line;
    } else {
      current = { label: null, body: line, items: [] };
      sections.push(current);
    }
  }
  return (
    sections
      .filter((s) => s.label || s.body || s.items.length)
      // The generator ends with a per-file list, but the panel opens over a
      // table that already shows every file. Repeating them underneath read as
      // a duplicate, so the section is dropped here rather than re-prompting
      // the model — the text itself is unchanged.
      .filter((s) => !/^files?$/i.test((s.label || '').trim()))
  );
}

const SparkIcon: React.FC<{ size?: number }> = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" />
  </svg>
);

const ChevronIcon: React.FC<{ up: boolean }> = ({ up }) => (
  <svg
    width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    style={{ transform: up ? 'none' : 'rotate(180deg)', transition: 'transform .18s ease' }}
  >
    <path d="M6 15l6-6 6 6" />
  </svg>
);

const VaultSummarySheet: React.FC<Props> = ({ titleKey = 'vault.summaryTitle', folderName, summary, updatedAt, open, onToggle }) => {
  const { t } = useLanguage();
  const sections = useMemo(() => parse(summary), [summary]);

  // Escape closes the panel — the same reflex that closes every other overlay
  // in the app, and the only way out for someone not using a mouse.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onToggle(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onToggle]);

  const teaser = useMemo(() => {
    const first = sections.find((s) => s.body) || sections[0];
    return (first?.body || first?.items[0] || summary).slice(0, 160);
  }, [sections, summary]);

  const when = updatedAt ? new Date(updatedAt).toLocaleString() : null;

  return (
    <>
      <button
        type="button"
        className={`vault-summary-bar ${open ? 'is-open' : ''}`}
        onClick={() => onToggle(!open)}
        aria-expanded={open}
      >
        <span className="vsb-icon"><SparkIcon /></span>
        <span className="vsb-label">{t(titleKey)}</span>
        <span dir="auto" className="vsb-teaser">{teaser}</span>
        <span className="vsb-chevron"><ChevronIcon up={!open} /></span>
      </button>

      <div className={`vault-summary-sheet ${open ? 'is-open' : ''}`} aria-hidden={!open}>
        <div className="vss-head">
          <div className="vss-title">
            <SparkIcon size={17} />
            <div>
              <h4>{t(titleKey)}</h4>
              <span dir="auto" className="vss-sub">
                {folderName}
                {when ? ` · ${t('vault.summaryUpdated', { when })}` : ''}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="vss-close"
            onClick={() => onToggle(false)}
            aria-label={t('vault.summaryClose')}
          >
            <ChevronIcon up={false} />
          </button>
        </div>

        <div className="vss-body">
          {sections.length === 0 ? (
            <p className="vss-raw">{summary}</p>
          ) : (
            sections.map((section, i) => (
              <div className="vss-section" key={`${section.label ?? 'p'}-${i}`}>
                {section.label && <div className="vss-label" dir="auto">{section.label}</div>}
                {section.body && CHIP_LABELS.test(section.label || '') ? (
                  <div className="vss-chips">
                    {section.body
                      .split(',')
                      .map((chip) => chip.trim())
                      .filter(Boolean)
                      .map((chip, n) => (
                        <span className="vss-chip" key={n}>{chip}</span>
                      ))}
                  </div>
                ) : (
                  section.body && <p className="vss-text">{section.body}</p>
                )}
                {section.items.length > 0 && (
                  <ul className="vss-items">
                    {section.items.map((item, n) => {
                      const split = item.match(/^([^:]{1,80}):\s*(.*)$/);
                      return (
                        <li key={n}>
                          {split ? (
                            <>
                              <span className="vss-item-name">{split[1]}</span>
                              <span className="vss-item-text">{split[2]}</span>
                            </>
                          ) : (
                            <span className="vss-item-text">{item}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default VaultSummarySheet;
